import {
    ORDERED_TRAFFIC_ENGAGEMENT_METRICS,
    ORDERED_TRAFFIC_OVERALL_CHANGE_METRICS,
    ORDERED_TRAFFIC_SOURCE_TYPE_METRICS,
} from "app/pages/sales-intelligence/sub-modules/signals/constants/traffic/trafficMetrics";
import TrafficChangesPeriod from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesPeriod";
import TrafficChangesTrend from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesTrend";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import { UNKNOWN_ALIAS, UNKNOWN_SIGNAL_TRANSLATABLE } from "../../../constants/common";
import SignalSource from "../../../models/core/SignalSource";
import SignalTemplatesManager from "../../../models/templates/SignalTemplatesManager";
import type TrafficSignalFilters from "../../../models/traffic/TrafficSignalFilters";
import TrafficSignalSubType from "../../../models/traffic/TrafficSignalSubType";
import TrafficSignalTemplate from "../../../models/traffic/TrafficSignalTemplate";

import { TrafficTemplateAlias } from "./types";

export default class TrafficSignalTemplatesManager extends SignalTemplatesManager<TrafficSignalFilters> {
    static get optionsByAlias(): Record<TrafficTemplateAlias, readonly string[]> {
        return {
            [TrafficTemplateAlias.OVERALL_CHANGE]: ORDERED_TRAFFIC_OVERALL_CHANGE_METRICS,
            [TrafficTemplateAlias.CHANGE_BY_MARKETING_CHANNEL]: ORDERED_TRAFFIC_SOURCE_TYPE_METRICS,
            [TrafficTemplateAlias.ENGAGEMENT]: ORDERED_TRAFFIC_ENGAGEMENT_METRICS,
            [TrafficTemplateAlias.NEW_MARKETS]: [
                ...ORDERED_TRAFFIC_OVERALL_CHANGE_METRICS,
                ...ORDERED_TRAFFIC_SOURCE_TYPE_METRICS,
            ],
        };
    }

    private getCountryCode(): number {
        const worldwideCountry = this.countries.find(({ id }) => id === WORLDWIDE_COUNTRY_ID);

        if (typeof worldwideCountry !== "undefined") {
            return worldwideCountry.id;
        }

        return this.countries[0]?.id ?? WORLDWIDE_COUNTRY_ID;
    }

    getEmptyTemplate(): Readonly<TrafficSignalTemplate> {
        return new TrafficSignalTemplate({
            alias: UNKNOWN_ALIAS,
            name: UNKNOWN_SIGNAL_TRANSLATABLE,
            signalSource: SignalSource.Platform,
            description: UNKNOWN_SIGNAL_TRANSLATABLE,
            filters: {},
        });
    }

    getDefaultTemplate(): Readonly<TrafficSignalTemplate> {
        return this.getEmptyTemplate();
    }

    getTemplates(): readonly TrafficSignalTemplate[] {
        const countryCode = this.getCountryCode();

        return [
            new TrafficSignalTemplate({
                alias: TrafficTemplateAlias.OVERALL_CHANGE,
                name: {
                    key: `si.signals.templates.traffic.${TrafficTemplateAlias.OVERALL_CHANGE}.name`,
                },
                description: {
                    key: `si.signals.templates.traffic.${TrafficTemplateAlias.OVERALL_CHANGE}.description`,
                },
                signalSource: SignalSource.Platform,
                filters: {
                    trafficChanges: {
                        metric: undefined,
                        value: undefined,
                        direction: TrafficChangesTrend.Grew,
                        period: TrafficChangesPeriod.MonthOverMonth,
                        country: countryCode,
                    },
                },
                signalSubType: TrafficSignalSubType.GettingTraction,
                optionsConfig: {
                    trafficChanges: {
                        metrics: TrafficSignalTemplatesManager.optionsByAlias[TrafficTemplateAlias.OVERALL_CHANGE],
                    },
                },
            }),
            new TrafficSignalTemplate({
                alias: TrafficTemplateAlias.CHANGE_BY_MARKETING_CHANNEL,
                name: {
                    key: `si.signals.templates.traffic.${TrafficTemplateAlias.CHANGE_BY_MARKETING_CHANNEL}.name`,
                },
                description: {
                    key: `si.signals.templates.traffic.${TrafficTemplateAlias.CHANGE_BY_MARKETING_CHANNEL}.description`,
                },
                signalSource: SignalSource.Platform,
                filters: {
                    trafficChanges: {
                        metric: undefined,
                        value: undefined,
                        direction: TrafficChangesTrend.Grew,
                        period: TrafficChangesPeriod.MonthOverMonth,
                        country: countryCode,
                    },
                },
                signalSubType: TrafficSignalSubType.GettingTraction,
                signalLifecycleState: "updated",
                optionsConfig: {
                    trafficChanges: {
                        metrics:
                            TrafficSignalTemplatesManager.optionsByAlias[
                                TrafficTemplateAlias.CHANGE_BY_MARKETING_CHANNEL
                            ],
                    },
                },
            }),
            new TrafficSignalTemplate({
                alias: TrafficTemplateAlias.ENGAGEMENT,
                name: {
                    key: `si.signals.templates.traffic.${TrafficTemplateAlias.ENGAGEMENT}.name`,
                },
                description: {
                    key: `si.signals.templates.traffic.${TrafficTemplateAlias.ENGAGEMENT}.description`,
                },
                signalSource: SignalSource.Platform,
                filters: {
                    trafficChanges: {
                        metric: undefined,
                        value: undefined,
                        direction: TrafficChangesTrend.Grew,
                        period: TrafficChangesPeriod.MonthOverMonth,
                        country: countryCode,
                    },
                },
                signalSubType: TrafficSignalSubType.Engagement,
                signalLifecycleState: "post-beta",
                optionsConfig: {
                    trafficChanges: {
                        metrics: TrafficSignalTemplatesManager.optionsByAlias[TrafficTemplateAlias.ENGAGEMENT],
                    },
                },
            }),
            new TrafficSignalTemplate({
                alias: TrafficTemplateAlias.NEW_MARKETS,
                name: {
                    key: `si.signals.templates.traffic.${TrafficTemplateAlias.NEW_MARKETS}.name`,
                },
                description: {
                    key: `si.signals.templates.traffic.${TrafficTemplateAlias.NEW_MARKETS}.description`,
                },
                signalSource: SignalSource.Platform,
                filters: {
                    newMarkets: {
                        metric: undefined,
                        thresholdValue: TrafficSignalTemplate.defaultValues.newMarkets.thresholdValue,
                        countries: undefined,
                    },
                },
                signalSubType: TrafficSignalSubType.NewMarkets,
                optionsConfig: {
                    newMarkets: {
                        metrics: TrafficSignalTemplatesManager.optionsByAlias[TrafficTemplateAlias.NEW_MARKETS],
                    },
                },
            }),
        ];
    }
}
